import { EventEmitter } from "events";

import { hasRole, hasRoles, RolesResolver } from "@egis-auth/authorization";
import { DecodedJwt, decodeJwt } from "@egis-auth/jwt-utils";

import AbstractTokenStorage from "./storages/AbstractTokenStorage";
import MemoryTokenStorage from "./storages/MemoryTokenStorage";
import { AuthenticationApi, StadeCardChangePasswordForm } from "./generated";

export interface BasicUserJwt extends DecodedJwt {
  iat: number;
  exp: number;
}

export interface AuthenticationServiceOptions<
  JwtType extends DecodedJwt = BasicUserJwt
> {
  endpoint: string;
  storage?: AbstractTokenStorage<JwtType>;
}

export default class AuthenticationService<
  JwtType extends DecodedJwt = BasicUserJwt
> extends EventEmitter {
  protected tokenStorage: AbstractTokenStorage<JwtType>;
  protected swaggerApi: AuthenticationApi;
  protected expiryTimeout:
    | ReturnType<typeof setTimeout>
    | undefined = undefined;

  constructor(options?: AuthenticationServiceOptions<JwtType>) {
    super();
    this.tokenStorage = options?.storage || new MemoryTokenStorage<JwtType>();
    this.swaggerApi = new AuthenticationApi({
      basePath: options?.endpoint || "http://localhost"
    });
  }

  private _setToken(token: string) {
    const decoded = decodeJwt(token);
    if (decoded.exp) {
      // remove 10 extra seconds to allow JWT renewal before its expiry
      const delay = Date.now() - decoded.exp - 10000;
      this.expiryTimeout = setTimeout(() => {
        this.emit("jwt-expired");
        this.expiryTimeout = undefined;
      }, delay);
    }
    this.tokenStorage.setToken(token);
  }

  public getToken(): JwtType {
    return this.tokenStorage.getToken();
  }

  /**
   * Get the raw token in the localStorage.
   *
   * @returns The raw token.
   * @throws Will throw an error if the token is not found in the local storage.
   */
  public getRawToken(): string {
    return this.tokenStorage.getRawToken();
  }

  public logout(): void {
    return this.tokenStorage.removeToken();
  }

  /**
   * Check if the user is logged in.
   *
   * @returns `true` if the user is logged in, `false` if not.
   */
  public isLoggedIn(): boolean {
    try {
      this.getToken();
    } catch (error) {
      return false;
    }
    return true;
  }

  /**
   * Check if the token is expired.
   *
   * @returns `true` if the token is expired, `false` if not.
   * @throws Will throw an error if the token is not found in the local storage.
   * @throws Will throw an error if the exp field of the token is `undefined`.
   * @throws Will throw an error if the exp field of the token is `null`.
   * @throws Will throw an error if the exp field of the token is not a number.
   */
  public isTokenExpired(): boolean {
    try {
      const token = this.getToken();

      if (token.exp === undefined) {
        throw new Error("The exp field of the token is undefined.");
      }

      if (token.exp === null) {
        throw new Error("The exp field of the token is null.");
      }

      if (typeof token.exp !== "number") {
        throw new Error("The exp field of the token is not a number.");
      }

      return +token.exp < Date.now();
    } catch (error) {
      throw error;
    }
  }

  public async cardLogin(
    username: string,
    password: string,
    sn: string,
    cardId: string,
    options?: any
  ) {
    const response = await this.swaggerApi.stadeCardLogin(
      username,
      password,
      sn,
      cardId,
      options
    );

    if (response.status >= 400) {
      throw new Error(response.statusText);
    }

    if (!response.data.token) {
      throw new Error("Token not found");
    }

    /**
     * @todo: Better errors processing
     */
    if (response.data.mustChangePassword === true) {
      throw new MustChangePasswordError(response.data.token);
    }

    await this._setToken(response.data.token);

    return response.data.token;
  }

  public hasRole(role: string) {
    try {
      return hasRole(this.getToken().roles, role);
    } catch (err) {
      return false;
    }
  }

  public hasRoles(roleOrResolver: string | RolesResolver) {
    try {
      return hasRoles(this.getToken().roles, roleOrResolver);
    } catch (err) {
      return false;
    }
  }

  public async changePassword(
    token: string,
    payload: StadeCardChangePasswordForm
  ) {
    const response = await this.swaggerApi.stadeCardChangePassword(payload, {
      headers: {
        "X-Bearer-Token": `Bearer ${token}`
      }
    });

    if (response.status >= 400) {
      throw new Error(response.statusText);
    }

    return response;
  }
}

export class MustChangePasswordError extends Error {
  private _token: string;
  public get token() {
    return this._token;
  }

  constructor(token: string) {
    super("Authentication denied - Password must be changed.");
    this._token = token;
  }
}
